import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ProjectOrderInput, ProjectOrderInputs } from '../../api/models/project-summary';
import { RoleRequest } from '../../api/models/role-assignment-map';
import { SearchInput, SearchResultBase } from '../../api/models/search-result';
import { AuthService } from '../../api/services/auth.service';
import { MarketService } from '../../api/services/market.service';
import { PreferencesService } from '../../api/services/preferences.service';
import { ProductAliasService } from '../../api/services/product-alias.service';
import { ProjectService } from '../../api/services/project.services';
import { SearchService } from '../../api/services/search.service';
import { AnalyticsService } from '../../api/services/analytics.service';


@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit, OnDestroy {

  projectNumMap: any;
  accountNo: string;
  searchResultBase: SearchResultBase;
  page: number = 1;
  pageSize = 10;
  loading = false;
  backToSearch: string;
  noResultMsg = ['No results found!'];

  pcFiltersShowMoreToggle: boolean = false;
  marketFiltersShowMoreToggle: boolean = false;

  defaultProjectIcon = '&#xe' + (2306).toString(16);
  isTesscoAdmin: boolean;
  isClientAdmin: boolean;

  @ViewChildren("indexFilterCheckBoxes") indexFilterCheckBoxes: QueryList<ElementRef>;
  @ViewChildren("productCategoryFilterCheckBoxes") productCategoryFilterCheckBoxes: QueryList<ElementRef>;
  @ViewChildren("marketFilterCheckBoxes") marketFilterCheckBoxes: QueryList<ElementRef>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private searchService: SearchService,
    private marketService: MarketService,
    private projectService: ProjectService,
    private preferencesService: PreferencesService,
    private productAliasService: ProductAliasService,
    private authService: AuthService,
    private toastr: ToastrService,
    private analyticsService: AnalyticsService
  ) { }


  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.backToSearch = params['backToSearch'];
      if (!this.backToSearch) {
        this.searchService.saveSearchText(params['searchText']);
        this.searchService.saveOrgId(params['orgId']);
        let isTesscoAdmin = params['isTesscoAdmin'];
        this.searchService.saveTesscoAdmin(isTesscoAdmin ? JSON.parse(isTesscoAdmin) : false);
        this.searchService.saveSearchPage(1);
        this.clearAllFilters();
        this.page = 1;
      } else {
        this.page = this.searchService.getSearchPage();
      }

      if (this.searchService.getSearchText()) {
        this.searchService.$searchSubject.next(this.searchService.getSearchText());
      }
      
      this.accountNo = localStorage.getItem('marketAccountNo');
      if (this.searchService.getSearchText()) {
        this.loadPage(this.searchService.getSearchPage(), this.backToSearch ? false : true);
      }
    });
  }

  ngAfterViewInit() {
    if (this.backToSearch) {
      this.checkFilters();
    }
  }

  loadPage(page: number, isMainCall?: boolean) {
    let filterArray: string[] = []; // Initialize the array
    this.loading = true;
    this.searchService.saveSearchPage(page);
    this.projectNumMap = new Map();
    let searchInput = new SearchInput(this.searchService.getSearchText(),
                          this.accountNo,
                          this.page, this.pageSize, 
                          this.searchService.getTesscoAdmin() === false ? this.searchService.getOrgId() : null);
    if (this.searchService.getInputIndexFilters().length > 0) {
      /**
       * Need to clear opposite filter when only 1 specific index needs to be queried.
       * This is to avoid getting no result back since the opposite filter won't
       * fetch any result from search service.
       * Might need to do same thing if external product gets added.
       */
      if (this.searchService.getInputIndexFilters().length === 1) {
        if (this.searchService.getInputIndexFilters()[0] === 'product') {
          this.clearMarketCategoryFilter();
        } else if (this.searchService.getInputIndexFilters()[0] === 'project'
          || this.searchService.getInputIndexFilters()[0] === 'worksheet'
          || this.searchService.getInputIndexFilters()[0] === 'bom') {
          this.clearProductCategoryFilter();
        }
      }
      searchInput.setFilterIndexNames(this.searchService.getInputIndexFilters());
      filterArray.push(...this.searchService.getInputIndexFilters());
     }
    if (this.searchService.getInputMarketFilters().length > 0) {
      searchInput.setFilterMarketIds(this.searchService.getInputMarketFilters());
      filterArray.push('market');

    }
    if (this.searchService.getInputProductCategoryFilters().length > 0) {
      searchInput.setFilterProductCategories(this.searchService.getInputProductCategoryFilters());
      filterArray.push('product_category');
    }
    if (this.searchService.getSortBy()) {
      searchInput.setSortBy(this.searchService.getSortBy());
    }

    this.searchService.query(searchInput).subscribe(result => {
      this.searchResultBase = result;
      this.loading = false;
      this.setUpAfterSearch(isMainCall);
      this.setFocusOnSearchResults();
      this.sendDataToAnalytics(filterArray);
    },
    error => {
      this.loading = false;
      window.scrollTo(0, 80);
    });

  }

  private sendDataToAnalytics(filterArray: string[]) {
    this.analyticsService.trackGlobalSearch(this.searchService.getSearchText(), this.formatArrayToString(filterArray));
  }

  setUpAfterSearch(isMainCall: boolean) {
    let marketIdsToFetch: string[] = [];
    let productSkus: string[] = [];
    if (this.searchResultBase && this.searchResultBase.results && this.searchResultBase.results.length) {

      if (this.searchResultBase.countByMarketIds && JSON.stringify(this.searchResultBase.countByMarketIds) !== '{}') {
        for (let key in this.searchResultBase.countByMarketIds) {
          this.pushUniqueValueToStringArray(key, marketIdsToFetch);
        }
      } else {
        this.clearMarketCategoryFilter();
      }
      if (this.searchResultBase.countByProdCategories && JSON.stringify(this.searchResultBase.countByProdCategories) !== '{}') {
        for(let key in this.searchResultBase.countByProdCategories) {
          if (!this.searchService.getProductCategoryFilters().find(k => k.category === key)) {
            this.searchService.getProductCategoryFilters().push({ category: key, count: this.searchResultBase.countByProdCategories[key] });
          }
        }
      } else {
        this.clearProductCategoryFilter();
      }

      this.searchResultBase.results.filter(r => r.index === 'product').forEach(r => {
        this.pushUniqueValueToStringArray(r.productTesscoPartNumber, productSkus);
      });

      if (productSkus.length) {
        this.productAliasService.productDetailsV2(productSkus, this.accountNo, '', true).subscribe(products => {
          if (products) {
            products.forEach(p => {
              this.searchResultBase.results
                .filter(r => r.index === 'product'
                  && r.productTesscoPartNumber === p.sku)
                .forEach(r => {
                  r.productListPrice = p.listPrice;
                  r.productImage = p.imageUrl;
                });
            });
          }
          });  
        }
      this.searchResultBase.results.filter(r => r.index === 'project').forEach(r => {
        this.pushUniqueValueToStringArray(r.projectMarketId, marketIdsToFetch);
      });
      
      this.searchResultBase.results.filter(r => r.index === 'project').forEach(r => {
        this.preferencesService.getPreferences('PROJECT', r.id).subscribe(pref => {
          this.projectNumMap.set(r.id , pref['oracle-project-number'].Value);
        });
      });

      if (marketIdsToFetch.length) {
        this.marketService.getMarketsByIds(marketIdsToFetch).subscribe(markets => {
          if (markets) {
            markets.forEach(m => {
              if (!this.searchService.getMarketFilters().find(market => market.marketId === m.id)) {
                this.searchService.getMarketFilters().push({ marketId: m.id, marketName: m.name, count: this.searchResultBase.countByMarketIds[m.id] });
              }
              this.searchResultBase.results.filter(r => r.index === 'project' && r.projectMarketId === m.id)
                                            .forEach(r => r.projectMarketName = m.name);
            });
          }
        });
      }
    } else {
      this.clearProductCategoryFilter();
      this.clearMarketCategoryFilter();
    }
  }

  filterByIndex(event) {
    if (event.target.checked === true) {
      if (!this.searchService.getInputIndexFilters().includes(event.target.value)) {
        this.searchService.getInputIndexFilters().push(event.target.value);
      }
    } else {
      if (this.searchService.getInputIndexFilters().includes(event.target.value)) {
        this.searchService.getInputIndexFilters().splice(this.searchService.getInputIndexFilters().indexOf(event.target.value), 1 );
      }
    }
    this.page = 1;
    this.loadPage(1);
  }

  filterByProductCategory(event) {
    if (event.target.checked === true) {
      if (!this.searchService.getInputProductCategoryFilters().includes(event.target.value)) {
        this.searchService.getInputProductCategoryFilters().push(event.target.value);
      }
    } else {
      if (this.searchService.getInputProductCategoryFilters().includes(event.target.value)) {
        this.searchService.getInputProductCategoryFilters().splice(this.searchService.getInputProductCategoryFilters().indexOf(event.target.value), 1 );
      }
    }
    this.page = 1;
    this.loadPage(1);
  }

  filterByMarket(event) {
    if (event.target.checked === true) {
      if (!this.searchService.getInputMarketFilters().includes(event.target.value)) {
        this.searchService.getInputMarketFilters().push(event.target.value);
      }
    } else {
      if (this.searchService.getInputMarketFilters().includes(event.target.value)) {
        this.searchService.getInputMarketFilters().splice(this.searchService.getInputMarketFilters().indexOf(event.target.value), 1 );
      }
    }
    this.page = 1;
    this.loadPage(1);
  }

  clearAllFilters() {
    this.searchService.saveInputIndexFilters([]);
    this.searchService.saveInputMarketFilters([]);
    this.searchService.saveInputProductCategoryFilters([]);
    this.searchService.saveProductCategoryFilters([]);
    this.searchService.saveMarketFilters([]);
    this.searchService.saveSortBy('Relevance');
    this.pcFiltersShowMoreToggle = false;
    this.marketFiltersShowMoreToggle = false;

    this.indexFilterCheckBoxes && this.indexFilterCheckBoxes.forEach((element) => {
      element.nativeElement.checked = false;
    });

    this.productCategoryFilterCheckBoxes && this.productCategoryFilterCheckBoxes.forEach((element) => {
      element.nativeElement.checked = false;
    });
    
    this.marketFilterCheckBoxes && this.marketFilterCheckBoxes.forEach((element) => {
      element.nativeElement.checked = false;
    });
  }

  clearProductCategoryFilter() {
    this.searchService.saveInputProductCategoryFilters([]);
    this.searchService.saveProductCategoryFilters([]);
    this.pcFiltersShowMoreToggle = false;
    this.productCategoryFilterCheckBoxes && this.productCategoryFilterCheckBoxes.forEach((element) => {
      element.nativeElement.checked = false;
    });
  }

  clearMarketCategoryFilter() {
    this.searchService.saveInputMarketFilters([]);
    this.searchService.saveMarketFilters([]);
    this.marketFiltersShowMoreToggle = false;
    this.marketFilterCheckBoxes && this.marketFilterCheckBoxes.forEach((element) => {
      element.nativeElement.checked = false;
    });
  }

  checkFilters() {
    if (this.indexFilterCheckBoxes) {
      this.indexFilterCheckBoxes.changes.subscribe(() => {
        this.indexFilterCheckBoxes.forEach((element) => {
          if (this.searchService.getInputIndexFilters().includes(element.nativeElement.value)) {
            element.nativeElement.checked = true;
          }
        });
      });
    }

    if (this.marketFilterCheckBoxes) {
      this.marketFilterCheckBoxes.changes.subscribe(() => {
        this.marketFilterCheckBoxes.forEach((element) => {
          if (this.searchService.getInputMarketFilters().includes(element.nativeElement.value)) {
            element.nativeElement.checked = true;
          }
        });
      });
    }

    if (this.productCategoryFilterCheckBoxes) {
      this.productCategoryFilterCheckBoxes.changes.subscribe(() => {
        this.productCategoryFilterCheckBoxes.forEach((element) => {
          if (this.searchService.getInputProductCategoryFilters().includes(element.nativeElement.value)) {
            element.nativeElement.checked = true;
          }
        });
      });
    }
  }

  searchWithSort(sortBy: string) {
    this.searchService.saveSortBy(sortBy);
    this.page = 1;
    this.loadPage(1);
  }

  navigateTo(item: { index: string, id: string, productTesscoPartNumber: string, worksheetProjectName: string, worksheetIsQuoted: boolean, worksheetOrderNumber: string }) {
    if (item.index === 'product') {
      this.accountNo = localStorage.getItem('marketAccountNo');
      this.router.navigate(['/product-details', item.productTesscoPartNumber, this.accountNo], 
          { queryParams: { backToSearch: 'y' } });
    } else if (item.index === 'project') {
      this.router.navigate(['/dashboard', { outlets: { 'dashboard-content': ['my-oasis-details', item.id] } }], 
          { queryParams: { backToSearch: 'y' } });
    } else if (item.index === 'bom') {
      this.navigateToBom(item.id);
    } else if (item.index === 'worksheet') {
      this.navigateToWorksheet(item);
    }
  }

  navigateToBom(bomId: string){
    this.projectService.getBom(bomId).subscribe(bom => {
      this.authService.checkSpecificRoleForUser(bom.createdBy, 'ADMIN_TESSCO').subscribe(result => {
        let isCreatedByTesscoAdmin = false;
        if(result != null && result == 'true'){
          isCreatedByTesscoAdmin = true;
        }

        let roleRequest = new RoleRequest('ADMIN_TESSCO', null);
        roleRequest.setRoleAndContextMap('ADMIN_CLIENT', null);
        
        this.authService.getUserRoles(roleRequest).subscribe(roles => {
          this.isTesscoAdmin = roles.getRoleMap('ADMIN_TESSCO');
          this.isClientAdmin = roles.getRoleMap('ADMIN_CLIENT');
    
          if(this.isTesscoAdmin){
            this.router.navigate(['/boms'], { queryParams: { bomId: bomId, backToSearch: 'y' } });
          } else {
            if((this.isClientAdmin && !isCreatedByTesscoAdmin) || this.authService.getUserId() === bom.createdBy){
              this.router.navigate(['/boms'], { queryParams: { bomId: bomId, backToSearch: 'y' } });
            } else {
              this.showErrorMessage();
            }
          }
        });
      });
    }, error => {
      this.showErrorMessage();
    });
  }
  
  navigateToWorksheet(item: { index: string, id: string, productTesscoPartNumber: string, worksheetProjectName: string, worksheetIsQuoted: boolean, worksheetOrderNumber: string }) {
    if (!(item.worksheetIsQuoted && item.worksheetIsQuoted === true) && (item.worksheetOrderNumber === undefined || item.worksheetOrderNumber === null)) {
      this.router.navigate(['/dashboard', { outlets: { 'dashboard-content': ['order-worksheet', item.id] } }],
      { queryParams: { backToSearch: 'y' } });
    } else if (item.worksheetOrderNumber === undefined || item.worksheetOrderNumber === null) {
      this.router.navigate(['/quote-process', { outlets: { 'quote-process-content': ['checkout', item.id] } }],
      { queryParams: { backToSearch: 'y' } });
    } else {
      let projectOrderInputs = new ProjectOrderInput(item.worksheetProjectName, this.accountNo, item.worksheetOrderNumber, localStorage.getItem("marketLocationCode"))
      this.projectService.getOrderConfirmationV2(projectOrderInputs).subscribe(result => {
        let confirmedOrder = result.orders[0];
        if(confirmedOrder && item.worksheetProjectName && this.accountNo){
          this.router.navigate(['/dashboard', { outlets: { 'dashboard-content': ['order-confirmation', item.worksheetProjectName, this.accountNo, item.worksheetOrderNumber] } }],
          { queryParams: { backToSearch: 'y' } });
        }else {
          this.router.navigate(['/quote-process', { outlets: { 'quote-process-content': ['confirmedOrder', item.id] } }],
          { queryParams: { backToSearch: 'y' } });
        }
      });
    }
  }

  setFocusOnSearchResults() {
    let body = document.body, html = document.documentElement;
    let height = Math.max( body.scrollHeight, body.offsetHeight, 
                       html.clientHeight, html.scrollHeight, html.offsetHeight );
    window.scrollTo({
      top: height * 0.15,
      left: 0,
      behavior: 'smooth'
    });
  }

  pushUniqueValueToStringArray(value: string, arr: string[]) {
    if (!arr.includes(value)) {
      arr.push(value);
    }
  }

  formatString(string: string): string {
    return string === 'bom' ? 'BOM' : string.charAt(0).toUpperCase() + string.substr(1);
  }

  replaceLineBreak(s:string) {
    return s && s.replace(/<[^>]*>/g, ' ');
  }

  ngOnDestroy() {
    this.searchService.$searchCleanSubject.next();
  }

  setDefaultProductPic(event) {
    event.target.src = 'assets/images/no-image.jpg';
  }

  private showErrorMessage() {
    this.toastr.error('User is not authorized to access this BOM.', 'Error', {
      timeOut: 2000,
      positionClass: 'toast-bottom-right'
    });
  }

   formatArrayToString(items: string[]): string {
    if (!items || items.length === 0) {
      return "";
    }
  
    if (items.length === 1) {
      return items[0];
    }
  
    const allButLast = items.slice(0, -1); 
    const lastItem = items[items.length - 1];
    return allButLast.join(", ") + " and " + lastItem;
  }
}




// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/app/search/search.component.ts